import { useState, useCallback, useEffect, useRef } from "react";
import { companyService } from "../services";
import toast from "react-hot-toast";

export const useCompanies = (params = {}, user = null) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Data state
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Loading states for mutations
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);

  // Debounce and throttling
  const debounceTimeoutRef = useRef(null);
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const REQUEST_THROTTLE_MS = 1000; // Minimum 1 second between requests
  const MAX_RETRIES = 3;

  // Fetch companies with throttling
  const fetchCompanies = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;

    // Throttle requests to prevent rate limiting
    if (timeSinceLastRequest < REQUEST_THROTTLE_MS) {
      console.log("Request throttled, waiting...");
      return;
    }

    lastRequestTimeRef.current = now;
    setIsLoading(true);

    try {
      // Build API parameters based on user role
      const apiParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      };

      // For Company Owner, only fetch their own company
      if (user?.role?.id === 2 && user?.company?.id) {
        apiParams.companyId = user.company.id;
      }

      const response = await companyService.getAll(apiParams);

      setCompanies(response.data.companies || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      // Handle rate limiting with retry
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`
          );

          setTimeout(() => {
            fetchCompanies();
          }, retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please wait a moment and try again.");
          retryCountRef.current = 0;
          return;
        }
      }

      // Reset retry count on other errors
      retryCountRef.current = 0;

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch companies"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm]);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for search
    debounceTimeoutRef.current = setTimeout(() => {
      fetchCompanies();
    }, 500); // 500ms debounce for search

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Load companies on mount and when page changes (no debounce for page changes)
  useEffect(() => {
    fetchCompanies();
  }, [currentPage]);

  // Manual refresh function that doesn't depend on useEffect
  const refreshCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await companyService.getAll({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      });

      setCompanies(response.data.companies || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch companies"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, params]);

  // Create company
  const createCompany = useCallback(
    async (data) => {
      setIsCreating(true);
      try {
        await companyService.create(data);
        toast.success("Company created successfully!");
        setShowModal(false);
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create company"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchCompanies]
  );

  // Update company
  const updateCompany = useCallback(
    async (id, data) => {
      setIsUpdating(true);
      try {
        await companyService.update(id, data);
        toast.success("Company updated successfully!");
        setShowModal(false);
        setEditingCompany(null);
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update company"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchCompanies]
  );

  // Delete company
  const deleteCompany = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await companyService.delete(id);
        toast.success("Company deleted successfully!");
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete company"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchCompanies]
  );

  // Toggle status
  const toggleStatus = useCallback(
    async (id) => {
      setIsToggling(true);
      try {
        await companyService.toggleStatus(id);
        toast.success("Company status updated successfully!");
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update company status"
        );
      } finally {
        setIsToggling(false);
      }
    },
    [fetchCompanies]
  );

  // Upload logo
  const uploadLogo = useCallback(
    async (id, logoFile) => {
      setIsUploadingLogo(true);
      try {
        await companyService.uploadLogo(id, logoFile);
        toast.success("Company logo uploaded successfully!");
        setSelectedLogo(null);
        setLogoPreview(null);
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to upload logo"
        );
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [fetchCompanies]
  );

  // Delete logo
  const deleteLogo = useCallback(
    async (id) => {
      setIsDeletingLogo(true);
      try {
        await companyService.deleteLogo(id);
        toast.success("Company logo deleted successfully!");
        await fetchCompanies(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete logo"
        );
      } finally {
        setIsDeletingLogo(false);
      }
    },
    [fetchCompanies]
  );

  // Handlers
  const handleSubmit = useCallback(
    (data) => {
      if (editingCompany) {
        updateCompany(editingCompany.id, data);
      } else {
        createCompany(data);
      }
    },
    [editingCompany, updateCompany, createCompany]
  );

  const handleEdit = useCallback((company) => {
    setEditingCompany(company);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (company) => {
      if (window.confirm("Are you sure you want to delete this company?")) {
        deleteCompany(company.id);
      }
    },
    [deleteCompany]
  );

  const handleToggleStatus = useCallback(
    (company) => {
      toggleStatus(company.id);
    },
    [toggleStatus]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingCompany(null);
    setSelectedLogo(null);
    setLogoPreview(null);
  }, []);

  const handleLogoSelect = useCallback(
    (event, companyId) => {
      const file = event.target.files[0];
      if (file) {
        setSelectedLogo(file);
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);

        // Auto-upload only if we're in the table view (companyId provided)
        if (companyId) {
          uploadLogo(companyId, file);
        }
      }
    },
    [uploadLogo]
  );

  const handleLogoUpload = useCallback(
    (companyId) => {
      if (selectedLogo) {
        uploadLogo(companyId, selectedLogo);
      }
    },
    [selectedLogo, uploadLogo]
  );

  const handleLogoDelete = useCallback(
    (companyId) => {
      if (
        window.confirm("Are you sure you want to delete this company logo?")
      ) {
        deleteLogo(companyId);
      }
    },
    [deleteLogo]
  );

  const handleRemoveLogoPreview = useCallback(() => {
    setSelectedLogo(null);
    setLogoPreview(null);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Manual retry function for rate-limited requests
  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchCompanies();
  }, [fetchCompanies]);

  // Filter companies based on search term
  const filteredCompanies = companies.filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.phone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    // State
    searchTerm,
    setSearchTerm,
    currentPage,
    showModal,
    setShowModal,
    editingCompany,
    selectedLogo,
    logoPreview,

    // Data
    companies: filteredCompanies,
    isLoading,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,
    isUploadingLogo,
    isDeletingLogo,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handleLogoSelect,
    handleLogoUpload,
    handleLogoDelete,
    handleRemoveLogoPreview,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createCompany,
    updateCompany,
    deleteCompany,
    toggleStatus,
    uploadLogo,
    deleteLogo,
    fetchCompanies,
    refreshCompanies,
  };
};

export default useCompanies;
